import Carousel, { Pagination, ICarouselInstance } from "react-native-reanimated-carousel";
import React, { useCallback, useMemo, useRef } from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { View } from "native-base";
import { HugeiconsIcon } from "@hugeicons/react-native";
import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons-pro/core-solid-rounded";
import { useSharedValue } from "react-native-reanimated";

import { useAppContext } from "../API/Context-API/AppContext";
import { scaleAll_ } from "../API/Scale";
import { AutoHeighImage } from "../Image/AutoWidthImage";
import type { CarouselImagesProps } from "./CarouselImages";

const CarouselImagesNative = ({
  data = [],
  addButtons = false,
  width = 300,
  height = 400,
  addPagination = true,
  renderItem = null,
  carouselProps,
}: CarouselImagesProps) => {
  const { currentWidth, scaleAll, isSmallVersion } = useAppContext();

  const hasMultipleSlides = data.length > 1;
  const shouldShowPagination = addPagination && hasMultipleSlides;
  const shouldShowButtons = addButtons && hasMultipleSlides;

  const progress = useSharedValue(0);
  const refCarousel = useRef<ICarouselInstance>(null);

  const defaultRenderItem = useCallback(
    ({ item }: { item: any; index: number }) => {
      return (
        <AutoHeighImage
          width={Math.min(currentWidth - 40, 1000 * scaleAll)}
          scaleAll={scaleAll}
          source={{
            uri: item,
          }}
          maxHeight={400}
        />
      );
    },
    [currentWidth, scaleAll]
  );

  const handlePrev = useCallback(() => {
    refCarousel.current?.prev();
  }, []);

  const handleNext = useCallback(() => {
    refCarousel.current?.next();
  }, []);

  const handlePaginationPress = useCallback((targetIndex: number) => {
    const carousel = refCarousel.current;
    if (!carousel) return;

    const currentIndex = carousel.getCurrentIndex();
    const diff = targetIndex - currentIndex;

    carousel.scrollTo({
      count: diff,
      animated: true,
    });
  }, []);

  const paginationContainerStyle = useMemo(
    () => ({
      gap: 5 * scaleAll,
      marginTop: 10 * scaleAll,
    }),
    [scaleAll]
  );

  const paginationDotStyle = useMemo(
    () => ({
      height: isSmallVersion ? 5 : 10,
      width: isSmallVersion ? 5 : 10,
      borderRadius: isSmallVersion ? 5 : 10,
      backgroundColor: "lightgrey",
    }),
    [isSmallVersion]
  );

  const mergedCarouselProps = useMemo(
    () => ({
      windowSize: 1,
      enabled: hasMultipleSlides,
      loop: hasMultipleSlides,
      ...carouselProps,
    }),
    [carouselProps, hasMultipleSlides]
  );

  return (
    <View style={styles.container}>
      {shouldShowButtons && (
        <>
          <TouchableOpacity
            onPress={handlePrev}
            style={[styles.button, styles.leftButton]}
            children={
              <HugeiconsIcon
                icon={ArrowLeft01Icon}
                color="black"
                size={scaleAll_(30)}
              />
            }
          />
          <TouchableOpacity
            onPress={handleNext}
            style={[styles.button, styles.rightButton]}
            children={
              <HugeiconsIcon
                icon={ArrowRight01Icon}
                color="black"
                size={scaleAll_(30)}
              />
            }
          />
        </>
      )}

      <Carousel
        ref={refCarousel}
        width={width}
        onProgressChange={shouldShowPagination ? progress : undefined}
        data={data}
        height={height}
        renderItem={renderItem ?? defaultRenderItem}
        {...mergedCarouselProps}
      />

      {shouldShowPagination && (
        <Pagination.Custom
          data={data}
          onPress={handlePaginationPress}
          progress={progress}
          containerStyle={paginationContainerStyle}
          dotStyle={paginationDotStyle}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
  },
  button: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 30,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  leftButton: {
    left: 0,
  },
  rightButton: {
    right: 0,
  },
});

export default React.memo(CarouselImagesNative);
